import { type Context, type FunctionComponent, useState, useRef, useContext } from 'react';

import { useNavigate } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { CUBOH_TAGS } from 'constants/cuboh';

import { getIntegrationsByStatus } from 'helpers/menuManagement';

import { useMenuManagement } from 'hooks/useMenuManagement';
import { useAuth } from 'hooks/useAuth';
import { usePusher } from 'hooks/usePusher';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { breakpoints } from 'styles/theme.variables';

import { HeadquarterContext } from 'context/HeadquarterContext';

import { Button, Center, Group, Loader, Menu, Text } from '@mantine/core';

import { getPosSyncScenario, POS_SYNC_COPY } from 'constants/posSyncCopy';

import PublishAllAlert from 'components/Modals/PublishAllAlert/PublishAllAlert';
import { PublishMenuModal } from 'components/Modals/PublishMenuModal';
import RefreshPOSModal from 'components/Modals/RefreshPOSModal/RefreshPOSModal';

import { ChevronDownIconXs } from '@chownow/pantry/icons';
import MenuManagementActionsWrapper from '../MenuActions/MenuManagementActionsWrapper';
import { ImportMenuDrawer } from '../MenuActions/ImportMenuDrawer';
import type { ImportsInProgress } from '../MenuActions/ImportMenuDrawer';
import MenuInformation from '../MenuInformation';
import { CreateMenuDropdown } from './CreateMenuDropdown';
import { PublishControl } from './PublishControl';
import { MENU_DROPDOWN_WIDTH } from './helpers';

const PUBLISH_ALL_MENUS_ID = 0;

type MenuActionsDropdownProps = {
  companyId: string;
  isBuildActive?: boolean;
  merchantId: string;
};

type MenuInformationContextType = {
  setCreateMenu: (merchantId: string) => void;
};

export const MenuActionsDropdown: FunctionComponent<MenuActionsDropdownProps> = ({
  companyId,
  isBuildActive = false,
  merchantId,
}) => {
  const { setCreateMenu } = useContext<MenuInformationContextType>(
    MenuInformation.Context as Context<MenuInformationContextType>,
  );
  const { getMerchantIntegrationsFromState, fetchMerchantIntegrations, sortedMenus } =
    useMenuManagement();
  const { merchants, companies, flags } = useAuth();
  const PUBLISH_LOCKED = flags?.PUBLISH_LOCKED;
  const { menuUploadWizard } = useFlags();

  const { isSingleMerchantType } = useContext(HeadquarterContext);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`, undefined, {
    getInitialValueInEffect: false,
  });

  const { channels } = usePusher();
  const navigate = useNavigate();

  const [drawerOpened, drawerHandlers] = useDisclosure();
  const [alertOpened, alertHandlers] = useDisclosure();
  const [publishMenuOpened, publishMenuHandlers] = useDisclosure();
  const [refreshPOSOpened, refreshPOSHandlers] = useDisclosure();
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState<boolean>(false);
  const [importsInProgress, setImportsInProgress] = useState<ImportsInProgress>({});

  const previouslyFetchedImportsRef = useRef<string[]>([]);

  const merchantIntegrationsState = getMerchantIntegrationsFromState(merchantId || '');
  const shouldShowRefreshPOS = merchantIntegrationsState?.enforcePosConditions;
  const posSyncScenario = getPosSyncScenario(
    !!merchantIntegrationsState?.isPosSyncInPlace,
    !!merchantIntegrationsState?.isPosAutoPublishOnSync,
  );
  // undefined until menus are loaded - copy falls back to the plural form
  const menuCount = sortedMenus?.[merchantId]?.length;

  const handleMenuOpen = async (): Promise<void> => {
    if ((!companyId && !merchantId) || previouslyFetchedImportsRef.current.includes(merchantId)) {
      return;
    }

    setIsLoadingIntegrations(true);
    try {
      await fetchMerchantIntegrations(companyId, merchantId);

      const { data } = await getIntegrationsByStatus(
        companyId,
        merchantId,
        CUBOH_TAGS.IMPORT_IN_PROGRESS,
      );
      setImportsInProgress(data.integrations);
    } catch (error) {
      setImportsInProgress({});
    } finally {
      setIsLoadingIntegrations(false);
      previouslyFetchedImportsRef.current.push(merchantId);
    }
  };

  const handleOpenPublish = (): void => {
    alertHandlers.close();
    publishMenuHandlers.open();
  };

  const modals = (
    <>
      <ImportMenuDrawer
        opened={drawerOpened}
        close={drawerHandlers.close}
        importsInProgress={importsInProgress}
        merchantId={merchantId}
      />
      <PublishAllAlert
        opened={alertOpened}
        close={alertHandlers.close}
        handleOnConfirm={handleOpenPublish}
      />
      <PublishMenuModal
        opened={publishMenuOpened}
        close={publishMenuHandlers.close}
        channels={channels}
        companies={companies}
        menu={{
          companyId,
          merchantId,
          uuid: PUBLISH_ALL_MENUS_ID,
        }}
        merchants={merchants}
      />
      <RefreshPOSModal
        opened={refreshPOSOpened}
        close={refreshPOSHandlers.close}
        merchantId={merchantId}
        scenario={posSyncScenario}
        menuCount={menuCount}
      />
    </>
  );

  if (menuUploadWizard) {
    const controls = (
      <>
        <PublishControl
          isSingleMerchantType={isSingleMerchantType}
          isMobile={isMobile ?? false}
          publishLocked={PUBLISH_LOCKED}
          showRefreshPOS={shouldShowRefreshPOS}
          posSyncScenario={posSyncScenario}
          menuCount={menuCount}
          importsInProgress={importsInProgress}
          onMenuOpen={() => void handleMenuOpen()}
          onPublish={alertHandlers.open}
          onRefreshPOS={refreshPOSHandlers.open}
        />
        <CreateMenuDropdown
          isSingleMerchantType={isSingleMerchantType}
          isMobile={isMobile ?? false}
          publishLocked={PUBLISH_LOCKED}
          isBuildActive={isBuildActive}
          isLoadingIntegrations={isLoadingIntegrations}
          merchantId={merchantId}
          onMenuOpen={() => void handleMenuOpen()}
          onOpenImportDrawer={drawerHandlers.open}
          setCreateMenu={setCreateMenu}
        />
      </>
    );

    return (
      <MenuManagementActionsWrapper merchantID={merchantId}>
        {isSingleMerchantType ? (
          <Group
            gap="xs"
            wrap="nowrap"
            style={isMobile ? { flexDirection: 'row-reverse' } : undefined}
          >
            {controls}
          </Group>
        ) : (
          <Button.Group w="fit-content">{controls}</Button.Group>
        )}
        {modals}
      </MenuManagementActionsWrapper>
    );
  }

  return (
    <MenuManagementActionsWrapper merchantID={merchantId}>
      <Menu position="bottom-end" onOpen={() => void handleMenuOpen()}>
        <Menu.Target>
          <Button
            variant={isSingleMerchantType ? 'filled' : 'outline'}
            size={isMobile ? 'sm' : 'md'}
            rightSection={<ChevronDownIconXs />}
          >
            Actions
          </Button>
        </Menu.Target>
        <Menu.Dropdown w={MENU_DROPDOWN_WIDTH}>
          {isLoadingIntegrations ? (
            <Center p="md">
              <Loader size="sm" />
            </Center>
          ) : (
            <>
              <Menu.Item
                disabled={PUBLISH_LOCKED}
                onClick={() => {
                  setCreateMenu(merchantId);
                  void navigate(`create?merchantId=${merchantId}`);
                }}
              >
                <Text variant="label-md">Create menu</Text>
              </Menu.Item>
              <Menu.Item onClick={drawerHandlers.open}>
                <Text variant="label-md">Import menus</Text>
              </Menu.Item>
              <Menu.Item onClick={alertHandlers.open}>
                <Text variant="label-md">Publish menus</Text>
              </Menu.Item>
              {shouldShowRefreshPOS && (
                <Menu.Item
                  disabled={Object.values(importsInProgress).includes('pos')}
                  onClick={refreshPOSHandlers.open}
                >
                  <Text variant="label-md">
                    {POS_SYNC_COPY[posSyncScenario].getButtonLabel(menuCount)}
                  </Text>
                </Menu.Item>
              )}
            </>
          )}
        </Menu.Dropdown>
      </Menu>
      {modals}
    </MenuManagementActionsWrapper>
  );
};
